/**
 * Copyright IBM Corp. 2022, 2026
 * SPDX-License-Identifier: MPL-2.0
 */

import { useEffect, useState } from 'react'
import classNames from 'classnames'
import useProductMeta, { Products as HashiCorpProduct } from '@web/product-meta'
import { parseUrl } from '@hashicorp/react-components/src/lib/parse-url'
import s from './alert-banner.module.css'
import { trackClick } from './analytics'
import { FlightIcon } from '@hashicorp/mds-react/components'
import { vercelSplit } from '@web/cms'

interface AlertBannerProps {
	tag: string
	text: string
	url: string
	expirationDate?: string
	hideOnMobile?: boolean
	linkText?: string
	product?: HashiCorpProduct
}

function AlertBanner({
	expirationDate,
	hideOnMobile,
	linkText,
	product = 'vagrant', // override useProductMeta's default "hashicorp", as it would result in a dull gray background
	tag,
	text,
	url,
}: AlertBannerProps): React.ReactElement {
	const [isShown, setIsShown] = useState(() => {
		const hasExpired = expirationDate && Date.now() > Date.parse(expirationDate)

		return !hasExpired
	})
	const { themeClass } = useProductMeta(vercelSplit(product))
	const { href } = parseUrl(url)

	/**
	 * On mount, hide the banner if it is expired
	 */
	useEffect(() => {
		const hasExpired = expirationDate && Date.now() > Date.parse(expirationDate)
		setIsShown((current) => (current !== !hasExpired ? !hasExpired : current))
	}, [expirationDate])

	return (
		<>
			<div
				className={classNames(
					s.root,
					themeClass,
					{ [s.isShown]: isShown },
					{ [s.hideOnMobile]: hideOnMobile },
				)}
				suppressHydrationWarning
			>
				<a
					href={href}
					className={s.linkElem}
					onClick={() => trackClick({ linkText, product, tag, text })}
				>
					<span className={s.textContainer}>
						<span className={s.tag}>{tag} </span>
						<span className={s.text}>
							{text}
							{linkText ? (
								<span className={s.link}>
									<span className={s.linkText}> {linkText}</span>
									<FlightIcon name="arrow-right" size={16} />
								</span>
							) : null}
						</span>
					</span>
				</a>
			</div>
		</>
	)
}

export { AlertBanner, type AlertBannerProps }
